import { useEffect, useRef, useState } from "react";
import { Autocomplete, Chip, CircularProgress, TextField, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { BookableBookingObjectDto } from "../typings/resource-calendar.types";

export type ScheduleResourceAutocompleteOption = BookableBookingObjectDto & {
  searchLabel: string;
};

type P = {
  isLoading: boolean;
  loadError: boolean;
  onResourceChange: (value: ScheduleResourceAutocompleteOption | null) => void;
  options: ScheduleResourceAutocompleteOption[];
  value: ScheduleResourceAutocompleteOption | null;
};

export function ScheduleResourceAutocomplete({
  isLoading,
  loadError,
  onResourceChange,
  options,
  value
}: Readonly<P>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const hasSelectedResource = value != null;
  const [emphasizeLoaded, setEmphasizeLoaded] = useState(false);
  const wasLoadingRef = useRef(isLoading);

  useEffect(() => {
    const prevLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoading;
    if (prevLoading && !isLoading && options.length > 0) {
      setEmphasizeLoaded(true);
      const id = globalThis.setTimeout(() => setEmphasizeLoaded(false), 900);
      return () => globalThis.clearTimeout(id);
    }
    return undefined;
  }, [isLoading, options.length]);

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      getOptionLabel={option => option.searchLabel}
      isOptionEqualToValue={(option, selected) =>
        option.bookingInventoryId === selected.bookingInventoryId &&
        option.bookingType === selected.bookingType
      }
      loading={isLoading}
      disabled={isLoading}
      options={options}
      value={value ? [value] : []}
      onChange={(_, selected) => {
        if (selected.length === 0) {
          onResourceChange(null);
          return;
        }
        // `multiple` is only for chip UI; keep a single resource (latest pick wins if list ever returns >1).
        onResourceChange(selected[selected.length - 1]);
      }}
      sx={{
        flex: "1 1 280px",
        minWidth: { xs: "100%", sm: 280 },
        maxWidth: "100%",
        // animate the loading wave
        "@keyframes scheduleResourceAutocompleteWave": {
          "0%": { transform: "translate(-100%, -50%)" },
          "100%": { transform: "translate(250%, -50%)" }
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: 1.5,
          ...(emphasizeLoaded && {
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "calc(50% + 2px)",
              height: "calc(100% - 4px)",
              width: "45%",
              background: `linear-gradient(
                90deg,
                transparent 0%,
                ${alpha(theme.palette.primary.main, 0.04)} 15%,
                ${alpha(theme.palette.primary.main, 0.24)} 50%,
                ${alpha(theme.palette.primary.main, 0.04)} 85%,
                transparent 100%
              )`,
              animation:
                "scheduleResourceAutocompleteWave 2.2s cubic-bezier(0.33, 0, 0.2, 1) forwards",
              pointerEvents: "none",
              borderRadius: "inherit"
            }
          })
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          // variant="outlined"
          fullWidth
          error={loadError}
          helperText={loadError ? t("_scheduleResourceCatalogLoadError") : undefined}
          placeholder={
            hasSelectedResource ? undefined : t("_scheduleResourceAutocomplete_placeholder")
          }
          slotProps={{
            input: {
              ...params.InputProps,
              sx: { height: "50px" },
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
      noOptionsText={t("No Options")}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={`${option.bookingType}-${option.bookingInventoryId}`}
            data-testid={`${option.bookingInventoryId}-select-chip`}
            label={isMobile ? option.objectName : option.searchLabel}
            sx={{
              maxWidth: { xs: "calc(100% - 8px)", sm: "100%" },
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis"
              }
            }}
          />
        ))
      }
    />
  );
}
